import axios, { AxiosResponse } from 'axios';
import { action, atom, onMount } from 'nanostores';
import {
  AccountReceivable,
  AccountReceivablePaginatedResponse,
  AccountReceivableStatus,
  AccountReceivablesService,
  CreateAccountReceivableWithCustomerRequest,
  CustomerType,
  SyncInvoicesFromQuickbooksRequest,
  SyncInvoicesFromStripeRequest,
} from '../api';
import { IntegrationType } from '../api/models/IntegrationType';
import { toast } from 'sonner';

/**
 * Loader state while loading projects
 */
export const $loadingAccountReceivables = atom<boolean>(false);

/**
 * The currently selected project.
 */
export const $accountreceivable = atom<AccountReceivable | undefined>();

/**
 * Result of the currently selected project
 */
// export const $projectResult = atom<string>('');

/**
 * The set of projects in the current scope (org/workspace)
 */
export const $accountreceivables = atom<AccountReceivablePaginatedResponse | undefined>(undefined);

/**
 * The set of projects in the current scope (org/workspace)
 */
// export const $externalSourceSyncStatuses = atom<ExternalSourceSync[]>([]);

/**
 * We use this to display whether the file has been downloaded.
 */
// export const $downloaded = atom<string>('');

/**
 * When the projects store is mounted, we'll connect events for
 * real-time notifications.
 */
// onMount($projects, () => {
//   signalRService.register('UpdateProjectStatus', (msg: string) => {
//     const [projectId, status] = msg.split('=');

//     updateProjectField(projectId, 'status', status as ProjectStatus);
//   });

//   signalRService.register('CompletedRows', (msg: string) => {
//     const [projectId, rowCount] = msg.split('=');

//     updateProjectField(projectId, 'processedRows', Number.parseInt(rowCount));
//   });

//   signalRService.register('UpdateExternalDataImportStatus', (msg: string) => {
//     const [projectId, status] = msg.split('=');

//     updateProjectExternalSourceSyncFields(
//       projectId,
//       'lastSyncStatus',
//       status as SyncStatus,
//     );
//   });

//   signalRService.register('CompletedExternalDataImportRows', (msg: string) => {
//     const [projectId, importedRecords, totalRecords] = msg.split('=');

//     updateProjectField(projectId, 'totalRows', Number.parseInt(totalRecords));

//     updateProjectExternalSourceSyncFields(
//       projectId,
//       'totalRecords',
//       totalRecords as unknown as number,
//     );

//     updateProjectExternalSourceSyncFields(
//       projectId,
//       'importedRecords',
//       importedRecords as unknown as number,
//     );
//   });
// });

/**
 * Main function to load projects.
 */
const loadAccountReceivables = async (organizationId: string, portfolioId: string) => {
  console.log('Retrieving account receivables...');
  $loadingAccountReceivables.set(true); // Set loading state to true

  await ensureOrganization(organizationId);
  await ensurePortfolio(portfolioId);

  var accountreceivables = await AccountReceivablesService.getAccountReceivables(portfolioId);
  $accountreceivables.set(accountreceivables);

  $loadingAccountReceivables.set(false); // Set loading state back to false
};

/**
 * Reload projects on workspace change
 */
$portfolio.subscribe(async po => {
  $accountreceivables.set({
    items: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const params = $router.get()?.params as Record<'organizationId', string>;

  if (
    params &&
    params.organizationId &&
    params.organizationId.trim().length > 0 &&
    po
  ) {
    await loadAccountReceivables(params.organizationId, po?.portfolio.id ?? '');
  }
});

/**
 * Watch for route changes; if we haven't loaded the account receivables, we try to load them.
 */
route.subscribe(async name => {
  if (name !== 'accountreceivables' || ($accountreceivables.value?.items?.length ?? 0) > 0) {
    return;
  }

  const params = $router.get()?.params as Record<'organizationId', string> &
    Record<'portfolioId', string>;

  await loadAccountReceivables(params.organizationId, params.portfolioId);
});

/**
 * Watch for route changes; load the current selected project result.
 */
// route.subscribe(async name => {
//   if (name !== 'project') {
//     return;
//   }

//   const params = $router.get()?.params as Record<'organizationId', string> &
//     Record<'workspaceId', string> &
//     Record<'projectId', string>;

//   await loadProjectResult(
//     params.organizationId,
//     params.workspaceId,
//     params.projectId,
//   );
// });

/**
 * Main function to load project result.
 */
// const loadProjectResult = async (
//   organizationId: string,
//   workspaceId: string,
//   projectId: string,
// ) => {
//   console.log('Retrieving project result...');

//   await ensureOrganization(organizationId);
//   await ensureWorkspace(workspaceId);
//   await ensureProject(projectId);
//   var projectResult = await ProjectsService.downloadResults(projectId);

//   $projectResult.set(projectResult);
// };

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
export const createAccountReceivable = action(
  $accountreceivable,
  'createAccountReceivable',
  async (
    store,
    name: string,
    portfolioId: string,
    loanId: string,
    firstName: string,
    lastName: string,
    middleName: string,
    contactEmail: string,
    contactPhone: string,
    invoiceDate: string,
    dueDate: string,
    amountPaid: number,
    amountDue: number,
    customerType: CustomerType,
    balance: number,
    streetAddress1: string,
    streetAddress2: string,
    city: string,
    state: string,
    postalCode: string,
    country: string,
    status: AccountReceivableStatus,
    quickBooksInvoiceLink: string,
    stripeInvoiceLink: string,
    customInvoiceLink: string,
  ) => {
    const request: CreateAccountReceivableWithCustomerRequest = {
      name: name,
      portfolioId: portfolioId,
      loanId: loanId,
      firstName: firstName,
      lastName: lastName,
      middleName: middleName,
      contactEmail: contactEmail,
      contactPhone: contactPhone,
      customerType: customerType,
      balance: balance,
      invoiceDate: invoiceDate,
      dueDate: dueDate,
      amountPaid: amountPaid,
      amountDue: amountDue,
      streetAddress1: streetAddress1,
      streetAddress2: streetAddress2,
      city: city,
      state: state,
      postalCode: postalCode,
      country: country,
      status: status,
      quickBooksInvoiceLink: quickBooksInvoiceLink,
      stripeInvoiceLink: stripeInvoiceLink,
      customInvoiceLink: customInvoiceLink,
    };

    const response = await AccountReceivablesService.createAccountReceivableWithCustomer(request);

    if (!response.accountReceivable) {
      // error is displayed by caller
      return response;
    }

    $accountreceivable.set(response.accountReceivable);

    const current = $accountreceivables.get();
    $accountreceivables.set({
      items: [response.accountReceivable, ...(current?.items ?? [])],
      totalCount: (current?.totalCount ?? 0) + 1,
      pageNumber: current?.pageNumber ?? 1,
      pageSize: current?.pageSize ?? 10,
      totalPages: current?.totalPages ?? 1,
      hasNextPage: current?.hasNextPage ?? false,
      hasPreviousPage: current?.hasPreviousPage ?? false
    });
    return response;
  },
);

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
// export const createProjectWithExternalDataSource = action(
//   $project,
//   'createProjectWithExternalDataSource',
//   async (
//     store,
//     name: string,
//     orgId: string,
//     workspaceId: string,
//     agents: string[],
//     externalDataSource: IntegrationType,
//     startDate: string,
//     endDate: string,
//     configurations: Configurations,
//   ) => {
//     const request: ProvisionProjectUsingExternalDataSourceRequest = {
//       name: name,
//       orgId: orgId,
//       workspaceId: workspaceId,
//       agents: agents,
//       externalDataSource: externalDataSource,
//       startDate: startDate,
//       endDate: endDate,
//       configurations,
//     };

//     const response =
//       await ProjectsService.provisionProjectUsingExternalDataSource(request);

//     if (!response.project) {
//       // TODO: Show error
//       return;
//     }

//     $project.set(response.project);

//     $projects.set([response.project, ...$projects.get()]);

//     // also fetch the source syncs statuses
//     var externalsyncs =
//       await ProjectsService.externalSourceSyncStatus(workspaceId);
//     $externalSourceSyncStatuses.set(externalsyncs);
//   },
// );

/**
 * Create a new workspace
 * TODO: Include profile selection/invites.
 */
// export const createProjectWithAudioFiles = action(
//   $project,
//   'createProjectWithAudioFiles',
//   async (
//     store,
//     name: string,
//     orgId: string,
//     workspaceId: string,
//     files: File[],
//     agents: string[],
//     configurations: Configurations,
//   ) => {
//     const audioFiles = await Promise.all(
//       files.map(async file => {
//         const base64File = await file2Base64(file);
//         return {
//           fileName: file?.name,
//           fileContentsBase64: base64File,
//           extension: file?.name?.split('.').pop() ?? 'mp3',
//         } as AudioFile;
//       }),
//     );

//     const request: ProvisionProjectUsingAudioFilesRequest = {
//       name: name,
//       orgId: orgId,
//       workspaceId: workspaceId,
//       audioFiles,
//       agents: agents,
//       configurations,
//     };

//     const response =
//       await ProjectsService.provisionProjectUsingAudioFiles(request);

//     if (!response.project) {
//       // error is displayed by caller
//       return response;
//     }

//     $project.set(response.project);

//     $projects.set([response.project, ...$projects.get()]);
//     return response;
//   },
// );

/**
 * Starts the processing on the project by making an API call to set the
 * status to ready.  Locally, the service will pick this up automatically.
 * In production, we'll kick off the processing job.
 */
// export const runProject = action(
//   $project,
//   'runProject',
//   async (store, projectId: string) => {
//     await ProjectsService.runProject(projectId);

//     // The initial state is ready.
//     updateProjectField(projectId, 'status', 'Ready');
//   },
// );

/**
 * Cancels the processing on the project by making an API call to set the
 * status to ready.  Locally, the service will pick this up automatically.
 * In production, we'll kick off the processing job.
 */
// export const cancelProject = action(
//   $project,
//   'cancelProject',
//   async (store, projectId: string) => {
//     await ProjectsService.cancelProject(projectId);

//     // The initial state is ready.
//     updateProjectField(projectId, 'status', 'Cancelled');
//   },
// );

/**
 * Delete's the selected project.
 */
export const deleteAccountReceivable = action(
  $accountreceivable,
  'deleteAccountReceivable',
  async (store, accountreceivableId: string) => {
    await AccountReceivablesService.deleteAccountReceivable(accountreceivableId);

    $accountreceivable.set(undefined);

    const accountreceivables = $accountreceivables.get();
    if (!accountreceivables?.items) return;

    const index = accountreceivables.items.findIndex(p => p.id === accountreceivableId);

    if (index > -1) {
      const updatedItems = [...accountreceivables.items];
      updatedItems.splice(index, 1);
      $accountreceivables.set({
        ...accountreceivables,
        items: updatedItems,
        totalCount: accountreceivables.totalCount ?? 0 - 1
      });
    }
  },
);

/**
 * Updates the status of the specified project.
 * @param projectId The ID of the project to update.
 * @param status The status to set for the project.
 */
// function updateProjectField<K extends keyof Project>(
//   projectId: string,
//   field: K,
//   value: Project[K],
// ) {
//   if ($project.get()?.id === projectId) {
//     const project = $project.get();

//     if (project) {
//       project[field] = value;

//       if (project) {
//         $project.set(project);
//       }
//     }
//   }

//   // Update the projects array
//   var projects = $projects.get();

//   const index = projects.findIndex(p => p.id === projectId);

//   projects[index][field] = value;

//   $projects.set([...projects]);
// }

/**
 * Updates the status of the specified project.
 * @param projectId The ID of the project to update.
 * @param status The status to set for the project.
 */
// function updateProjectExternalSourceSyncFields<
//   K extends keyof ExternalSourceSync,
// >(projectId: string, field: K, value: ExternalSourceSync[K]) {
//   // Update the projects array
//   var syncStatuses = $externalSourceSyncStatuses.get();

//   const index = syncStatuses.findIndex(s => s.project.id === projectId);

//   syncStatuses[index][field] = value;

//   $externalSourceSyncStatuses.set([...syncStatuses]);
// }

/**
 * Trigger the download of the results as a CSV.
 */
// export const downloadProjectResults = action(
//   $project,
//   'downloadProjectResults',
//   async (store, projectId: string) => {
//     axios(`${apiUrl}/projects/${projectId}/downloadresults`, {
//       headers: {
//         Authorization: `Bearer ${(await $user.get()?.getIdToken(true)) ?? ''}`,
//       },
//     }).then((response: AxiosResponse<any, any>) => {
//       const disposition = response.headers['content-disposition'] as string;

//       const filename = disposition.match(/filename=([^;]+)/)?.[1];

//       const blob = new Blob([response.data], { type: 'text/plain' });
//       // create file link in browser's memory
//       const href = URL.createObjectURL(blob);

//       // create "a" HTML element with href to file & click
//       const link = document.createElement('a');
//       link.href = href;
//       link.setAttribute('download', filename ?? `${projectId}-results.csv`);
//       document.body.appendChild(link);
//       link.click();

//       // clean up "a" element & remove ObjectURL
//       document.body.removeChild(link);
//       URL.revokeObjectURL(href);

//       $downloaded.set(projectId);

//       window.setTimeout(() => $downloaded.set(''), 3000);
//     });
//   },
// );

/**
 * Encode the download link to Base64 and send it to the SmartAnalyzer.
 */
// export const sendDownloadLinkToSmartAnalyzer = action(
//   $project,
//   'sendDownloadLinkToSmartAnalyzer',
//   async (store, projectId: string) => {
//     try {
//       const response = await axios(
//         `${apiUrl}/projects/${projectId}/downloadresults`,
//         {
//           headers: {
//             Authorization: `Bearer ${(await $user.get()?.getIdToken(true)) ?? ''}`,
//           },
//           responseType: 'blob',
//         },
//       );

//       const blob = new Blob([response.data], { type: 'text/csv' });
//       const reader = new FileReader();

//       reader.onloadend = () => {
//         const result = reader.result;

//         if (typeof result === 'string') {
//           const base64data = result.split(',')[1];
//           if (base64data) {
//             const smartAnalyzerUrl = `/smartanalyze?csv=${encodeURIComponent(base64data)}`;

//             // Redirect to the SmartAnalyzer app
//             window.location.href = smartAnalyzerUrl;
//           } else {
//             console.error('Failed to extract Base64 data.');
//           }
//         } else {
//           console.error('Unexpected result type:', result);
//         }
//       };

//       reader.onerror = error => {
//         console.error('Failed to read the blob data:', error);
//       };

//       reader.readAsDataURL(blob);
//     } catch (error) {
//       console.error('Failed to download the project results:', error);
//     }
//   },
// );

/**
 * Encode the download link to Base64 and send it to the SmartAnalyzer.
 */
// export const smartAnalyzeCsv = action(
//   $project,
//   'smartAnalyzeCsv',
//   async (store, orgId: string, projectId?: string, file?: File) => {
//     let newFile: File | undefined = undefined;
//     if (projectId && !file) {
//       const response = await axios(
//         `${apiUrl}/projects/${projectId}/downloadresults`,
//         {
//           headers: {
//             Authorization: `Bearer ${(await $user.get()?.getIdToken(true)) ?? ''}`,
//           },
//           responseType: 'blob',
//         },
//       );

//       newFile = new File([response.data], 'filename.csv', {
//         type: 'text/csv',
//       });
//     } else if (!projectId && file) {
//       newFile = file;
//     }

//     const request: AnalyzeCsvRequest = {
//       fileContentsBase64: await file2Base64(newFile!),
//       orgId,
//     };

//     const analyzeCsvResponse: AnalyzeCsvResponse =
//       await SmartAnalyzeService.analyzeCsv(request);

//     return analyzeCsvResponse;
//   },
// );


export const createAccountReceivableComment = action(
  $accountreceivable,
  'createAccountReceivableComment',
  async (store, accountreceivableId: string, commentRequest: { commentText: string, files?: File[] }) => {
    // Convert Files to Blobs for the service
    const request = {
      commentText: commentRequest.commentText,
      files: commentRequest.files as Blob[] // Files are Blobs, so this is safe
    };

    const response = await AccountReceivablesService.createAccountReceivableComment(
      accountreceivableId, 
      request
    );

    if (!response.succeeded) {
      return response;
    }

    // Fetch fresh data to ensure we have all comments with attachments
    const freshData = await AccountReceivablesService.getAccountReceivable(accountreceivableId);
    if (freshData?.accountReceivable) {
      $accountreceivable.set(freshData.accountReceivable as AccountReceivable);

      const accountReceivables = $accountreceivables.get();
      if (!accountReceivables?.items) return;

      const index = accountReceivables.items.findIndex(ar => ar.id === accountreceivableId);
      if (index > -1) {
        const updatedItems = [...accountReceivables.items];
        updatedItems[index] = freshData.accountReceivable as AccountReceivable;
        $accountreceivables.set({
          ...accountReceivables,
          items: updatedItems
        });
      }
    }

    return response;
  }
);

/**
 * Ensure that the project is loaded and selected.
 */
export const ensureAccountReceivable = action(
  $accountreceivable,
  'ensureAccountReceivable',
  async (store, accountreceivableId: string) => {
    let accountReceivable: AccountReceivable | undefined;

    const params = $router.get()?.params as Record<'portfolioId', string>;

    const current = $accountreceivables.get();
    if (!current?.items?.length) {
      const accountreceivables = await AccountReceivablesService.getAccountReceivables(
        params.portfolioId ?? '',
      );

      $accountreceivables.set(accountreceivables);

      accountReceivable = accountreceivables.items?.find(p => p.id === accountreceivableId);
    } else {
      accountReceivable = current.items.find(p => p.id === accountreceivableId);
    }

    $accountreceivable.set(accountReceivable);
  },
);


// Store structure maps commentId -> attachmentId -> URL info
export const $commentAttachmentUrls = atom<{
  [commentId: string]: {
    [attachmentId: string]: {
      url: string;
      thumbnailUrl?: string;
      expiresAt: number;
    }
  }
}>({});

/**
 * Fetch and store the attachment URLs with expiration.
 */
export const fetchCommentAttachmentUrls = action(
  $commentAttachmentUrls,
  'fetchCommentAttachmentUrls',
  async (store, commentId: string, attachments: Array<{ id: string; mimeType: string }>) => {
    const imageAttachments = attachments.filter(a => a.mimeType.startsWith('image/'));
    if (!imageAttachments.length) return;

    const currentUrls = store.get();

    // Batch all requests into a single Promise.all
    const requests = imageAttachments.flatMap(attachment => [
      AccountReceivablesService.getAttachmentSignedUrl(commentId, attachment.id),
      AccountReceivablesService.getAttachmentThumbnailSignedUrl(commentId, attachment.id)
    ]);

    const results = await Promise.all(requests);
    
    // Process results in pairs (url, thumbnailUrl)
    const newUrls = Object.fromEntries(
      imageAttachments.map((attachment, index) => [
        attachment.id,
        {
          url: results[index * 2],
          thumbnailUrl: results[index * 2 + 1],
          expiresAt: Date.now() + 3600 * 1000
        }
      ])
    );

    store.set({
      ...currentUrls,
      [commentId]: {
        ...currentUrls[commentId],
        ...newUrls
      }
    });
  }
);

/**
 * Check if the stored URLs are expired and refetch if necessary.
 */
export const ensureCommentAttachmentUrls = action(
  $commentAttachmentUrls,
  'ensureCommentAttachmentUrls',
  async (store, commentId: string, attachments: Array<{ id: string; mimeType: string }>) => {
    const imageAttachments = attachments.filter(a => a.mimeType.startsWith('image/'));
    if (!imageAttachments.length) return;
    
    const currentUrls = store.get();
    const commentUrls = currentUrls[commentId];
    const now = Date.now();

    // Identify which attachments need updating
    const attachmentsToUpdate = imageAttachments.filter(attachment => 
      !commentUrls?.[attachment.id] || 
      now > (commentUrls[attachment.id]?.expiresAt ?? 0)
    );

    if (attachmentsToUpdate.length > 0) {
      // Fetch all URLs in parallel
      await Promise.all([
        fetchCommentAttachmentUrls(commentId, attachmentsToUpdate)
      ]);
    }
  }
);


export const syncInvoicesFromQuickbooks = action(
  $accountreceivables,
  'syncInvoicesFromQuickbooks',
  async (store, portfolioId: string, syncDate: Date, orgId: string) => {
    const request: SyncInvoicesFromQuickbooksRequest = {
      portfolioId: portfolioId,
      syncDate: syncDate.toISOString(),
      organizationId: orgId
    };
    const response = await AccountReceivablesService.syncInvoicesFromQuickbooks(request);
    if (response.succeeded) {
      const accountreceivables = await AccountReceivablesService.getAccountReceivables(portfolioId);
      $accountreceivables.set(accountreceivables);
    }
    return response;
  }
);

export const syncInvoicesFromStripe = action(
  $accountreceivables,
  'syncInvoicesFromStripe',
  async (store, dateFrom: Date, dateTo: Date, portfolioId: string, organizationId: string, syncVoluntaryChurnedSubscriptions: boolean, syncInvoluntaryChurnedSubscriptions: boolean, stripeProductIds: string[]) => {
    const request: SyncInvoicesFromStripeRequest = {
      dateFrom: dateFrom.toISOString(),
      dateTo: dateTo.toISOString(),
      portfolioId: portfolioId,
      organizationId: organizationId,
      syncVoluntaryChurnedSubscriptions: syncVoluntaryChurnedSubscriptions,
      syncInvoluntaryChurnedSubscriptions: syncInvoluntaryChurnedSubscriptions,
      stripeProductIds: stripeProductIds
    };
    const response = await AccountReceivablesService.syncInvoicesFromStripe(request);
    console.log("response", response);
    if (response.succeeded) {
      const accountreceivables = await AccountReceivablesService.getAccountReceivables(portfolioId);
      $accountreceivables.set(accountreceivables);
    }
    return response;
  }
);